import { extractVideoId, buildEmbedUrl } from '../../core/media/youtube-url.js';

/**
 * Videos field codec — wire⇄UI translation AND the single owner of the "valid / serializable
 * row" rule. The shared predicates (`isBlankRow`, `isInvalidRow`) are reused by the factory's
 * pre-submit validator so validation and serialization can never drift.
 *
 * Used internally by the crud-page factory to auto-inject the codec on `type: 'videos'` fields.
 *
 * @module
 */

/**
 * @typedef {import('../../contracts/forms.js').VideoEntry} VideoEntry
 */

/** Trimmed string accessor for a possibly-missing field. @param {Record<string, unknown>} o @param {string} k */
function str(o, k) {
  const v = o[k];
  return typeof v === 'string' ? v.trim() : '';
}

/**
 * A row is BLANK when it carries no id, no link, no title AND no description (all trimmed).
 * `youtube_link` must be in the test: a URL-only-but-invalid row is NOT blank — it must reach
 * `isInvalidRow` and block save, never be silently dropped.
 * @param {unknown} v
 */
export function isBlankRow(v) {
  if (!v || typeof v !== 'object') return true;
  const o = /** @type {Record<string, unknown>} */ (v);
  return !str(o, 'youtube_id') && !str(o, 'youtube_link') && !str(o, 'title') && !str(o, 'description');
}

/**
 * A row is INVALID when it is not blank AND its current URL does not parse to a valid id.
 * This blocks BOTH a garbage URL and a title/description-only row with no URL. It judges the
 * visible `youtube_link` only — never `youtube_id` — so a stale id from a prior valid URL
 * can't mask a now-invalid edit.
 * @param {unknown} v
 */
export function isInvalidRow(v) {
  if (isBlankRow(v)) return false;
  const o = /** @type {Record<string, unknown>} */ (v);
  return extractVideoId(str(o, 'youtube_link')) === null;
}

/**
 * Creates a FieldCodec for videos fields.
 *
 * - `parse(wire)`: array of backend rows → `VideoEntry[]`. Id precedence is HYDRATION-ONLY:
 *   a parsable `youtube_link` wins; else a stored `youtube_id` matching the id shape; else ''.
 *   When an id resolves, `youtube_link` is canonicalized to the embed URL.
 * - `serialize(ui)`: drop blank rows; emit `{ youtube_id, youtube_link, title, description,
 *   orderby }` per remaining row with `youtube_link = buildEmbedUrl(youtube_id)` and contiguous
 *   0-based `orderby` assigned AFTER filtering. Always returns an array (`[] → []`, never
 *   `undefined`) so clearing all videos persists and the key is always sent. Total — never
 *   throws on a partial/invalid row (an invalid row that reaches it drops like a blank one).
 *   Validation runs before serialize (see crud-editor submit order), so in practice it only
 *   ever sees a clean list.
 *
 * @returns {import('../../contracts/forms.js').FieldCodec}
 */
export function createVideosCodec() {
  return {
    /** @param {unknown} wireValue */
    parse(wireValue) {
      if (!Array.isArray(wireValue)) return [];
      return wireValue.map((row) => {
        const o = /** @type {Record<string, unknown>} */ (row && typeof row === 'object' ? row : {});
        const link = str(o, 'youtube_link');
        const storedId = str(o, 'youtube_id');
        // Precedence: parsable link → stored id (shape-checked) → none.
        const id = extractVideoId(link) ?? (/^[\w-]{11}$/.test(storedId) ? storedId : '');
        return {
          youtube_id: id,
          youtube_link: id ? buildEmbedUrl(id) : link,
          title: str(o, 'title'),
          description: str(o, 'description'),
        };
      });
    },

    /** @param {unknown} uiValue */
    serialize(uiValue) {
      if (!Array.isArray(uiValue)) return [];
      /** @type {Array<{ youtube_id: string, youtube_link: string, title: string, description: string, orderby: number }>} */
      const out = [];
      for (const row of uiValue) {
        if (isBlankRow(row)) continue;
        const o = /** @type {Record<string, unknown>} */ (row && typeof row === 'object' ? row : {});
        const id = extractVideoId(str(o, 'youtube_link')) ?? '';
        if (!id) continue; // invalid row — total/defensive; validation blocks these before save
        out.push({
          youtube_id: id,
          youtube_link: buildEmbedUrl(id),
          title: str(o, 'title'),
          description: str(o, 'description'),
          orderby: out.length, // contiguous 0-based, AFTER filtering
        });
      }
      return out;
    },
  };
}
